import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

/**
 * Story 5.8 — reusable add/remove/reorder editor for a `string[]` pool.
 *
 * Used by:
 *   - ReactivationConfigSection.tsx (reactivation message pool — Story 5.6)
 *   - (future) any other config field that holds an ordered free-form text list
 *
 * Pure presentational: parent owns the array state. Validation (e.g. ≥5
 * entries) is enforced at the parent level — the editor surfaces the
 * current count + per-item char count as guidance.
 */
export interface PoolMessageEditorProps {
  value: ReadonlyArray<string>
  onChange: (next: string[]) => void
  /** Render label above the editor (e.g. "Pool de Mensagens"). */
  label?: string
  /** Per-item char cap; renders a counter when set. Default 1000. */
  maxChars?: number
  /** Minimum entries hint (rendered below the list — does NOT block save). */
  minEntries?: number
  /** Disabled state — disables all controls (RBAC viewer/operator). */
  disabled?: boolean
}

export function PoolMessageEditor(props: PoolMessageEditorProps): React.ReactElement {
  const { value, onChange, label, maxChars = 1000, minEntries, disabled = false } = props

  function update(index: number, text: string): void {
    const next = value.slice()
    next[index] = text
    onChange(next)
  }

  function remove(index: number): void {
    onChange(value.filter((_, i) => i !== index))
  }

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta
    if (target < 0 || target >= value.length) return
    const next = value.slice()
    const a = next[index]!
    const b = next[target]!
    next[index] = b
    next[target] = a
    onChange(next)
  }

  function add(): void {
    onChange([...value, ''])
  }

  const belowMin = minEntries !== undefined && value.length < minEntries

  return (
    <div className="grid gap-2">
      {label !== undefined && (
        <label className="text-sm font-medium leading-none">{label}</label>
      )}
      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">Nenhuma mensagem ainda — clique em "Adicionar".</p>
      )}
      <ol className="grid gap-3">
        {value.map((msg, i) => (
          <li key={i} className="grid gap-1 rounded-md border bg-card/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Mover para cima"
                  disabled={disabled || i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Mover para baixo"
                  disabled={disabled || i === value.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover"
                  disabled={disabled}
                  onClick={() => remove(i)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
            <Textarea
              value={msg}
              onChange={(e) => update(i, e.target.value)}
              maxLength={maxChars}
              disabled={disabled}
              rows={3}
              placeholder="Texto da mensagem..."
            />
            <p className="text-right text-xs text-muted-foreground">
              {msg.length} / {maxChars}
            </p>
          </li>
        ))}
      </ol>
      <div className="flex items-center justify-between gap-3">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={add}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Adicionar
        </Button>
        {minEntries !== undefined && (
          <p
            className={`text-xs ${
              belowMin ? 'text-destructive' : 'text-muted-foreground'
            }`}
          >
            {value.length} / {minEntries}+ recomendado
          </p>
        )}
      </div>
    </div>
  )
}

export default PoolMessageEditor
